import React, { useState, useEffect } from 'react';
import { Upload, Plus, X, Image as ImageIcon, Move, Star } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import ImageEditor from '../../ImageEditor';

const ProductModal = ({ 
  isOpen, 
  product, 
  isDuplicate = false, 
  categories, 
  onClose, 
  onSave, 
  onUploadMainImage, 
  onUploadAdditionalImage, 
  onDeleteAdditionalImage, 
  onSaveImageAdjustment, 
  onSaveAdditionalImageAdjustment, 
  onRefreshProduct 
}) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    code: '',
    category_id: '',
    category_ids: [],
    min_quantity: 1,
    customizations: [],
    prices: [],
    active: true,
    featured: false
  });
  const [newCustomization, setNewCustomization] = useState('');
  const [newPrice, setNewPrice] = useState({ min_quantity: '', max_quantity: '', price: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  const [localImages, setLocalImages] = useState([]);
  const [localMainImage, setLocalMainImage] = useState(null);
  const [pendingMainImage, setPendingMainImage] = useState(null);
  const [pendingMainImagePreview, setPendingMainImagePreview] = useState(null);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [imageAdjustment, setImageAdjustment] = useState(null);
  const [imageAdjustments, setImageAdjustments] = useState([]);
  
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
  const [preUploadEditorOpen, setPreUploadEditorOpen] = useState(false);
  const [pendingAdjustment, setPendingAdjustment] = useState(null);

  useEffect(() => {
    if (product) {
      setForm({
        id: isDuplicate ? undefined : product.id,
        name: product.name || '',
        description: product.description || '',
        code: product.code || '',
        category_id: product.category_id || '',
        category_ids: product.category_ids || (product.category_id ? [product.category_id] : []),
        min_quantity: product.min_quantity || 1,
        customizations: product.customizations || [],
        prices: product.prices || [],
        active: product.active !== false,
        featured: product.featured || false
      });
      setLocalImages(isDuplicate ? [] : (product.images || []));
      setLocalMainImage(product.image_url);
      setImageAdjustment(isDuplicate ? null : (product.image_adjustment || null));
      setImageAdjustments(isDuplicate ? [] : (product.image_adjustments || []));
      setPendingMainImage(null);
      setPendingMainImagePreview(null);
    } else {
      setForm({
        name: '',
        description: '',
        code: '',
        category_id: '',
        category_ids: [],
        min_quantity: 1,
        customizations: [],
        prices: [],
        active: true,
        featured: false
      });
      setLocalImages([]);
      setLocalMainImage(null);
      setImageAdjustment(null);
      setImageAdjustments([]);
      setPendingMainImage(null);
      setPendingMainImagePreview(null);
    }
  }, [product, isDuplicate, categories]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file || !product?.id) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setPendingFile(file);
      setPendingPreviewUrl(reader.result);
      setPendingAdjustment({ scale: 1, position_x: 0, position_y: 0 });
      setPreUploadEditorOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleMainImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    
    if (product?.id) {
      setUploadingMainImage(true);
      try {
        await onUploadMainImage(product.id, file);
        await onRefreshProduct();
      } catch (error) {
        console.error('Error uploading main image:', error);
        alert('Erro ao fazer upload da imagem principal');
      } finally {
        setUploadingMainImage(false);
      }
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPendingMainImage(file);
        setPendingMainImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePendingMainImage = () => {
    setPendingMainImage(null);
    setPendingMainImagePreview(null);
  };

  const handleConfirmUpload = async (adjustment) => {
    if (!pendingFile || !product?.id) return;
    
    setPreUploadEditorOpen(false);
    setUploadingImage(true);
    
    const previewUrl = pendingPreviewUrl;
    setLocalImages(prev => [...prev, previewUrl]);
    setImageAdjustments(prev => [...prev, adjustment]);
    
    try {
      const result = await onUploadAdditionalImage(product.id, pendingFile);
      if (result && result.image_url) {
        setLocalImages(prev => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          updated[lastIndex] = result.image_url;
          return updated;
        });
        const newImageIndex = localImages.length;
        await onSaveAdditionalImageAdjustment(product.id, newImageIndex, adjustment);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setLocalImages(prev => prev.slice(0, -1));
      setImageAdjustments(prev => prev.slice(0, -1));
      alert('Erro ao fazer upload da imagem');
    } finally {
      setUploadingImage(false);
      setPendingFile(null);
      setPendingPreviewUrl(null);
      setPendingAdjustment(null);
    }
  };

  const handleCancelPreUpload = () => {
    setPreUploadEditorOpen(false);
    setPendingFile(null);
    setPendingPreviewUrl(null);
    setPendingAdjustment(null);
  };

  const handleDeleteImage = async (index) => {
    setLocalImages(prev => prev.filter((_, i) => i !== index));
    setImageAdjustments(prev => prev.filter((_, i) => i !== index));
    
    try {
      await onDeleteAdditionalImage(product.id, index);
    } catch (error) {
      console.error('Error deleting image:', error);
      onRefreshProduct?.();
    }
  };

  const openImageEditor = (index) => {
    setEditingImageIndex(index);
    setImageEditorOpen(true);
  };

  const handleSaveAdjustment = async (adjustment) => {
    if (!product?.id) return;
    
    try {
      if (editingImageIndex === null) {
        await onSaveImageAdjustment(product.id, adjustment);
        setImageAdjustment(adjustment);
        if (adjustment.croppedImage) {
          setLocalMainImage(adjustment.croppedImage);
        }
      } else {
        await onSaveAdditionalImageAdjustment(product.id, editingImageIndex, adjustment);
        setImageAdjustments(prev => {
          const newAdjustments = [...prev];
          while (newAdjustments.length <= editingImageIndex) {
            newAdjustments.push({ scale: 1, position_x: 0, position_y: 0 });
          }
          newAdjustments[editingImageIndex] = adjustment;
          return newAdjustments;
        });
        if (adjustment.croppedImage) {
          setLocalImages(prev => {
            const newImages = [...prev];
            newImages[editingImageIndex] = adjustment.croppedImage;
            return newImages;
          });
        }
      }
      setImageEditorOpen(false);
      setEditingImageIndex(null);
      if (onRefreshProduct) {
        await onRefreshProduct(product.id);
      }
    } catch (error) {
      console.error('Error saving adjustment:', error);
      alert('Erro ao salvar ajuste');
    }
  };

  const getImageUrl = () => {
    if (editingImageIndex === null) {
      return localMainImage;
    }
    return localImages[editingImageIndex];
  };

  const addCustomization = () => {
    if (newCustomization.trim()) {
      setForm(prev => ({
        ...prev,
        customizations: [...prev.customizations, newCustomization.trim()]
      }));
      setNewCustomization('');
    }
  };

  const removeCustomization = (index) => {
    setForm(prev => ({
      ...prev,
      customizations: prev.customizations.filter((_, i) => i !== index)
    }));
  };

  const addPrice = () => {
    if (newPrice.min_quantity && newPrice.price) {
      setForm(prev => ({
        ...prev,
        prices: [...prev.prices, {
          min_quantity: parseInt(newPrice.min_quantity),
          max_quantity: newPrice.max_quantity ? parseInt(newPrice.max_quantity) : null,
          price: parseFloat(newPrice.price)
        }].sort((a, b) => a.min_quantity - b.min_quantity)
      }));
      setNewPrice({ min_quantity: '', max_quantity: '', price: '' });
    }
  };

  const removePrice = (index) => {
    setForm(prev => ({
      ...prev,
      prices: prev.prices.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    if (!form.name || form.category_ids.length === 0) {
      alert('Nome e pelo menos uma categoria são obrigatórios');
      return;
    }
    const submitData = {
      ...form,
      category_id: form.category_ids[0] || ''
    };
    onSave(submitData, pendingMainImage);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isDuplicate ? 'Duplicar Produto' : (product?.id ? 'Editar Produto' : 'Novo Produto')}
          </DialogTitle>
          {isDuplicate && (
            <p className="text-sm text-slate-500 mt-1">
              Ajuste os dados do produto copiado antes de salvar
            </p>
          )}
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nome *</label>
              <Input 
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Código do Produto</label>
              <Input 
                value={form.code}
                onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
                placeholder="Ex: CAN-001, BRI-052"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Categorias *</label>
            <div className="flex flex-wrap gap-2 p-3 border border-slate-200 rounded-lg min-h-[44px]">
              {form.category_ids.map(catId => {
                const cat = categories.find(c => c.id === catId);
                return cat ? (
                  <span 
                    key={catId}
                    className="inline-flex items-center gap-1 bg-teal-100 text-teal-700 px-2 py-1 rounded-full text-sm"
                  >
                    {cat.name}
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({
                        ...prev,
                        category_ids: prev.category_ids.filter(id => id !== catId)
                      }))}
                      className="hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ) : null;
              })}
              <select 
                value=""
                onChange={(e) => {
                  if (e.target.value && !form.category_ids.includes(e.target.value)) {
                    setForm(prev => ({
                      ...prev,
                      category_ids: [...prev.category_ids, e.target.value]
                    }));
                  }
                }}
                className="border-0 bg-transparent text-sm focus:ring-0 outline-none min-w-[120px]"
              >
                <option value="">+ Adicionar categoria</option>
                {categories.filter(cat => !form.category_ids.includes(cat.id)).map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-slate-500 mt-1">Selecione uma ou mais categorias para o produto</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Descrição</label>
            <Textarea 
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
            />
          </div>

          {/* Main Image Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Imagem Principal</label>
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0 relative">
                {(pendingMainImagePreview || localMainImage) ? (
                  <>
                    <img 
                      src={pendingMainImagePreview || localMainImage}
                      alt="Imagem principal"
                      className="w-full h-full object-cover"
                    />
                    {pendingMainImagePreview && !product?.id && (
                      <button
                        type="button"
                        onClick={handleRemovePendingMainImage}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        title="Remover imagem"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <label className={`inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${uploadingMainImage ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleMainImageSelect}
                    className="hidden"
                    disabled={uploadingMainImage}
                  />
                  {uploadingMainImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-slate-600">Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {localMainImage || pendingMainImagePreview ? 'Trocar imagem' : 'Selecionar imagem'}
                      </span>
                    </>
                  )}
                </label>
                <p className="text-xs text-slate-500 mt-2">
                  {product?.id 
                    ? 'A imagem será atualizada imediatamente'
                    : 'A imagem será enviada após salvar o produto'
                  }
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Quantidade Mínima</label>
            <Input 
              type="number"
              min="1"
              value={form.min_quantity === '' ? '' : form.min_quantity}
              onChange={(e) => setForm(prev => ({ ...prev, min_quantity: e.target.value === '' ? '' : parseInt(e.target.value) || '' }))}
              onBlur={(e) => {
                if (e.target.value === '' || parseInt(e.target.value) < 1) {
                  setForm(prev => ({ ...prev, min_quantity: 1 }));
                }
              }}
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-slate-700">Produto Ativo</label>
              <p className="text-xs text-slate-500">Desative para ocultar o produto do catálogo</p>
            </div>
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, active: !prev.active }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.active ? 'bg-teal-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Featured Toggle */}
          <div className="flex items-center justify-between p-4 bg-[#195C75]/10 rounded-lg border border-[#195C75]/20">
            <div>
              <label className="block text-sm font-medium text-[#195C75] flex items-center gap-2">
                <Star className="w-4 h-4 text-[#195C75]" />
                Produto em Destaque
              </label>
              <p className="text-xs text-[#195C75]/70">Ative para mostrar na página inicial de Destaques</p>
            </div>
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, featured: !prev.featured }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.featured ? 'bg-[#195C75]' : 'bg-slate-300'}`}
              data-testid="featured-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.featured ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Customizations */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Opções de Personalização</label>
            <div className="flex gap-2 mb-2">
              <Input 
                value={newCustomization}
                onChange={(e) => setNewCustomization(e.target.value)}
                placeholder="Ex: Cores variadas"
                onKeyPress={(e) => e.key === 'Enter' && addCustomization()}
              />
              <Button onClick={addCustomization} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.customizations.map((item, idx) => (
                <span key={idx} className="bg-slate-100 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  {item}
                  <button onClick={() => removeCustomization(idx)} className="text-slate-400 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Prices */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tabela de Preços</label>
            <div className="flex gap-2 mb-2">
              <Input 
                type="number"
                placeholder="De (qtd)"
                value={newPrice.min_quantity}
                onChange={(e) => setNewPrice(prev => ({ ...prev, min_quantity: e.target.value }))}
                className="w-24"
              />
              <Input 
                type="number"
                placeholder="Até (qtd)"
                value={newPrice.max_quantity}
                onChange={(e) => setNewPrice(prev => ({ ...prev, max_quantity: e.target.value }))}
                className="w-24"
              />
              <Input 
                type="number"
                step="0.01"
                placeholder="Preço (R$)"
                value={newPrice.price}
                onChange={(e) => setNewPrice(prev => ({ ...prev, price: e.target.value }))}
                className="w-28"
              />
              <Button onClick={addPrice} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-1">
              {form.prices.map((price, idx) => (
                <div key={idx} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg text-sm">
                  <span>
                    {price.min_quantity}{price.max_quantity ? `-${price.max_quantity}` : '+'} un: R$ {price.price.toFixed(2)}/un
                  </span>
                  <button onClick={() => removePrice(idx)} className="text-slate-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Images Section */}
          {product?.id && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Galeria de Imagens ({localImages.length + 1} foto{localImages.length !== 0 ? 's' : ''})
              </label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {/* Main Image */}
                <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
                  <img 
                    src={localMainImage || 'https://via.placeholder.com/150?text=Principal'}
                    alt="Principal"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute bottom-1 left-1 text-[10px] bg-teal-600 text-white px-1.5 py-0.5 rounded">Principal</span>
                  {localMainImage && (
                    <button 
                      onClick={() => openImageEditor(null)}
                      className="absolute top-1 right-1 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Ajustar imagem"
                    >
                      <Move className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {/* Additional Images */}
                {localImages.map((img, idx) => (
                  <div key={idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
                    <img 
                      src={img} 
                      alt={`Foto ${idx + 2}`} 
                      className="w-full h-full object-cover"
                    />
                    <button 
                      onClick={() => openImageEditor(idx)}
                      className="absolute top-1 left-1 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Ajustar imagem"
                    >
                      <Move className="w-3 h-3" />
                    </button>
                    <button 
                      onClick={() => handleDeleteImage(idx)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remover imagem"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {/* Add Image Button */}
                <label className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-teal-500 hover:bg-teal-50 transition-colors">
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  {uploadingImage ? (
                    <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-6 h-6 text-slate-400" />
                      <span className="text-xs text-slate-400 mt-1">Adicionar</span>
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-slate-500">Selecione uma imagem para ajustar o enquadramento antes de adicionar</p>
            </div>
          )}

          {/* Pre-Upload Image Editor */}
          <ImageEditor
            isOpen={preUploadEditorOpen}
            imageUrl={pendingPreviewUrl}
            initialAdjustment={pendingAdjustment}
            onSave={handleConfirmUpload}
            onClose={handleCancelPreUpload}
            title="Ajustar Nova Imagem"
            confirmText="Adicionar"
          />

          {/* Image Editor Modal */}
          <ImageEditor
            isOpen={imageEditorOpen}
            imageUrl={getImageUrl()}
            initialAdjustment={editingImageIndex === null ? imageAdjustment : imageAdjustments[editingImageIndex]}
            onSave={handleSaveAdjustment}
            onClose={() => {
              setImageEditorOpen(false);
              setEditingImageIndex(null);
            }}
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700 text-white">
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
